import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { isManagerOrAbove } from '../../lib/roles';
import { subscribeToCustomersUpdates } from '../../services/socket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/format';
import { getTokens } from '../../utils/tokens';
import { Plus, Pencil, Trash2, Eye, X, Download, Loader2 } from 'lucide-react';
import { exportCustomersToExcel } from '../../utils/excelExport';
import {
  PageHeader, StatStrip, GhostBtn, PrimaryBtn, Toolbar,
  DataTable, Pagination, MonoMoney, AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const Customers = () => {
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);
  const { language } = useLanguage();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const canManage = isManagerOrAbove(user?.role);

  // ── style helpers ──────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20, minHeight: '100%',
    fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };
  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14,
    padding: 24, maxWidth: 500, width: '100%', margin: '0 16px',
    maxHeight: '90vh', overflowY: 'auto',
  };
  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken, border: `1px solid ${tk.border}`,
    borderRadius: 8, padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 6, display: 'block',
  };

  // ── computed stats ─────────────────────────────────────────────────────────
  const newThisMonth = customers.filter((c) => {
    if (!c.created_at) return false;
    const d = new Date(c.created_at);
    const now = new Date();
    return d >= new Date(now.getFullYear(), now.getMonth(), 1);
  }).length;

  // ── data ───────────────────────────────────────────────────────────────────
  useEffect(() => { loadCustomers(); }, [searchQuery]);

  useEffect(() => {
    const unsubscribe = subscribeToCustomersUpdates(loadCustomers);
    return unsubscribe;
  }, [searchQuery]);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getCustomers(searchQuery?.trim() || undefined, true);
      setCustomers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadCustomers'));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(customers.length / itemsPerPage);
  const paginatedCustomers = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return customers.slice(start, start + itemsPerPage);
  }, [customers, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const handleExport = async () => {
    if (!customers.length) return;
    setExporting(true);
    try {
      const labels = t('customers.excelExport');
      const L = typeof labels === 'object' && labels !== null ? labels : {};
      const today = new Date().toISOString().split('T')[0];
      await exportCustomersToExcel(customers, `customers-${today}.xlsx`, L);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const handleEditCustomer = (row) => setEditingCustomer(row);
  const handleDeleteCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) setDeletingCustomer(c);
  };

  // ── form modal ─────────────────────────────────────────────────────────────
  const CustomerFormModal = ({ customer, onClose, onSave }) => {
    const [formData, setFormData] = useState(
      customer
        ? { name: customer.name || customer.full_name || '', phone: customer.phone || '', address: customer.address || '' }
        : { name: '', phone: '', address: '' }
    );
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        if (!formData.name?.trim()) throw new Error(t('customers.nameRequired'));
        if (!formData.phone?.trim()) throw new Error(t('customers.phoneRequired'));
        if (formData.name.trim().length > 30) throw new Error(t('alerts.customerNameMaxLength'));
        if (formData.phone.trim().length > 15) throw new Error(t('alerts.customerPhoneMaxLength'));
        if ((formData.address || '').trim().length > 70) throw new Error(t('alerts.customerAddressMaxLength'));
        const phoneTrimmed = formData.phone.trim();
        const duplicate = customers.find((c) => c.phone === phoneTrimmed && c.id !== customer?.id);
        if (duplicate) throw new Error(t('customers.phoneUnique'));
        const payload = {
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          address: formData.address?.trim() || '',
        };
        if (customer) {
          await apiService.updateCustomer(customer.id, payload);
        } else {
          await apiService.createCustomer(payload);
        }
        onSave();
      } catch (err) {
        const apiError = err.body?.error || err.message;
        const showError = apiError && apiError.includes('already exists')
          ? t('customers.phoneUnique')
          : (apiError || t('alerts.failedToSave').replace('{error}', t('alerts.unknownError')));
        showToast(showError, "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {customer ? t('customers.editCustomer') : t('customers.addCustomer')}
            </div>
            <button type="button" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>{t('pos.customerName')}</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} maxLength={30} style={inputStyle} required />
            </div>
            <div>
              <label style={labelStyle}>{t('pos.customerPhone')}</label>
              <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })} maxLength={15} style={inputStyle} required />
            </div>
            <div>
              <label style={labelStyle}>{t('pos.customerAddress')}</label>
              <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} maxLength={70} rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
              <button type="button" onClick={onClose} disabled={saving} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={saving} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.accent, border: 'none', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                {saving ? t('common.loading') : customer ? t('common.update') : t('customers.addCustomer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ── view modal ─────────────────────────────────────────────────────────────
  const ViewModal = ({ customer, onClose }) => (
    <div style={overlay}>
      <div style={modalBox}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>{t('customers.customerDetails')}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: 14, background: tk.surfaceAlt, borderRadius: 10 }}>
          <AvatarInitials name={customer?.name || customer?.full_name || '?'} tk={tk} size={40} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>{customer?.name || customer?.full_name || '—'}</div>
            <div style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono",monospace' }}>{customer?.phone || '—'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { label: t('pos.customerAddress'), value: customer?.address || '—' },
            { label: t('customers.totalOrders'), value: String(customer?.order_count ?? 0) },
          ].map(({ label, value }) => (
            <div key={label}>
              <div style={labelStyle}>{label}</div>
              <div style={{ fontSize: 13, color: tk.text }}>{value}</div>
            </div>
          ))}
          <div>
            <div style={labelStyle}>{t('customers.totalPurchases')}</div>
            <MonoMoney amount={parseFloat(customer?.total_purchases || customer?.total_spent || 0)} tk={tk} size={14} weight={600} />
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <button onClick={onClose} style={{ width: '100%', height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── delete confirm modal ───────────────────────────────────────────────────
  const DeleteConfirmModal = ({ customer, onClose, onConfirm }) => (
    <div style={overlay}>
      <div style={modalBox}>
        <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>{t('customers.deleteCustomer')}</div>
        <div style={{ fontSize: 12.5, color: tk.textSecondary, marginBottom: 20 }}>
          {t('customers.deleteCustomerConfirm').replace('{name}', customer?.name || customer?.full_name || '')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
            {t('common.cancel')}
          </button>
          <button onClick={() => { onConfirm(customer.id); onClose(); }} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.danger, border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}>
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        tk={tk}
        title={t('customers.title') || t('nav.customers')}
        subtitle={t('customers.subtitle') || 'Customer database'}
        actions={<>
          <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
            {exporting ? t('monthlyClosing.downloading') : t('customers.downloadExcel')}
          </GhostBtn>
          {canManage && (
            <PrimaryBtn tk={tk} icon={Plus} onClick={() => setShowAddModal(true)}>
              {t('customers.addCustomer') || 'Add customer'}
            </PrimaryBtn>
          )}
        </>}
      />

      {error && (
        <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.danger}33`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <StatStripSkeleton tk={tk} count={2} />
      ) : (
        <StatStrip tk={tk} items={[
          { label: language === 'ar' ? 'الإجمالي' : 'Total', value: String(customers.length) },
          { label: language === 'ar' ? 'الجدد هذا الشهر' : 'New this month', value: String(newThisMonth) },
        ]} />
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('customers.searchPlaceholder') || 'Search customers...'}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[2, 1.5, 1, 1.5, 0.7]} />
      ) : (
        <DataTable
          tk={tk}
          emptyMessage={t('customers.noCustomers') || 'No customers found'}
          columns={[
            { key: 'name', label: language === 'ar' ? 'الاسم' : 'Name' },
            { key: 'phone', label: language === 'ar' ? 'الهاتف' : 'Phone' },
            { key: 'total_orders', label: language === 'ar' ? 'الطلبات' : 'Orders' },
            { key: 'total_spent', label: language === 'ar' ? 'الإنفاق' : 'Spent' },
            { key: 'actions', label: '' },
          ]}
          rows={paginatedCustomers}
          renderCell={(row, key) => {
            if (key === 'name') return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <AvatarInitials name={row.name || row.full_name || '?'} tk={tk} size={28} />
                <span style={{ fontWeight: 500, color: tk.text, fontSize: 12.5 }}>{row.name || row.full_name}</span>
              </div>
            );
            if (key === 'phone') return (
              <span style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 11.5, color: tk.textSecondary, direction: 'ltr' }}>{row.phone || '—'}</span>
            );
            if (key === 'total_orders') return (
              <span style={{ fontFamily: '"JetBrains Mono",monospace', fontWeight: 600, color: tk.text }}>{row.order_count ?? row.orders_count ?? 0}</span>
            );
            if (key === 'total_spent') return (
              <MonoMoney amount={parseFloat(row.total_purchases || row.total_spent || row.lifetime_value || 0)} tk={tk} size={12.5} weight={600} />
            );
            if (key === 'actions') return (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setViewingCustomer(row)} style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.accentText }}>
                  <Eye size={13} />
                </button>
                {canManage && <>
                  <button onClick={() => handleEditCustomer(row)} style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textSecondary }}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDeleteCustomer(row.id)} style={{ width: 28, height: 28, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.dangerText }}>
                    <Trash2 size={13} />
                  </button>
                </>}
              </div>
            );
            return <span style={{ color: tk.textSecondary, fontSize: 12 }}>{row[key] || '—'}</span>;
          }}
        />
      )}

      <Pagination tk={tk} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {showAddModal && (
        <CustomerFormModal
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); loadCustomers(); }}
        />
      )}
      {editingCustomer && (
        <CustomerFormModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={() => { setEditingCustomer(null); loadCustomers(); }}
        />
      )}
      {viewingCustomer && (
        <ViewModal customer={viewingCustomer} onClose={() => setViewingCustomer(null)} />
      )}
      {deletingCustomer && (
        <DeleteConfirmModal
          customer={deletingCustomer}
          onClose={() => setDeletingCustomer(null)}
          onConfirm={async (id) => {
            try {
              await apiService.deleteCustomer(id);
              loadCustomers();
            } catch (e) {
              const errMsg = e.body?.error || e.message || t('alerts.deleteFailed');
              const displayMsg = typeof errMsg === 'string' && errMsg.includes('orders_delivery_person_requires_customer')
                ? t('alerts.customerDeleteDeliveryPersonConflict')
                : errMsg;
              showToast(displayMsg, "error");
            }
          }}
        />
      )}
    </div>
  );
};

export default Customers;
